import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appNumberFormat]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberFormatDirective),
      multi: true
    }
  ]
})
export class NumberFormatDirective {
  private el = inject(ElementRef<HTMLInputElement>);

  private onChange: (value: number | null) => void = () => { };
  private onTouched: () => void = () => { };

  private rawString: string = '';
  private hasDecimal: boolean = false;
  private groupPattern: number[] | null = null;
  private thousandsSeparator: string | null = null;
  private decimalSeparator: string | null = null;
  private decimalPlaces: number | null = null;
  private maxIntegerDigits: number | null = null;

  ngOnInit() {
    this.setVariables();
  }

  setVariables() {
    const missingVariables = !this.groupPattern || !this.thousandsSeparator || !this.decimalSeparator || !this.decimalPlaces;
    if (missingVariables) {
      this.loadVariables();
    }
    else {
      const input = this.el.nativeElement;
      input.value = this.format(0);
    }
  }

  loadVariables() {
    //change this to get from api
    this.groupPattern = [3, 2, 2];
    this.maxIntegerDigits = 15;
    this.thousandsSeparator = ',';
    this.decimalSeparator = '.';
    this.decimalPlaces = 3;

    const input = this.el.nativeElement;
    input.value = this.format(0);
  }

  writeValue(value: number | null): void {
    if (value === null || value === undefined) {
      this.rawString = '';
      this.el.nativeElement.value = '';
      return;
    }

    this.rawString = value.toString();
    const [intPart, decPart = ''] = this.rawString.split('.');
    this.hasDecimal = this.rawString.includes('.');

    this.el.nativeElement.value = this.formatFromString(intPart, decPart);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  @HostListener('input')
  onInput(): void {
    const input = this.el.nativeElement;
    let value = input.value;
    const oldCursor = input.selectionStart ?? 0;
    const isCursorAtEnd = oldCursor === input.value.length;
    const rawBeforeCursor = input.value.slice(0, oldCursor);
    const hasDotBeforeCursor = rawBeforeCursor.includes(this.decimalSeparator!);
    const digitCursor = rawBeforeCursor.replace(/\D/g, '').length;
    if (!value || value.trim() === '') {
      this.rawString = '';
      this.hasDecimal = false;
      this.onChange(null);
      input.value = '';
      return;
    }
    value = value.replace(/[^\d.]/g, '');
    this.hasDecimal = value.includes(this.decimalSeparator!);
    let [intPartRaw = '', decPartRaw = ''] = value.split(this.decimalSeparator!);
    if (this.maxIntegerDigits && intPartRaw.length > this.maxIntegerDigits) {
      input.value = this.formatFromString(
        this.rawString.split('.')[0],
        this.rawString.split('.')[1] || ''
      );
      return;
    }
    let intPart = intPartRaw;
    let decPart = decPartRaw;
    if (intPart.length > 1 && intPart.startsWith('0')) {
      intPart = intPart.replace(/^0+/, '');
      if (intPart === '') intPart = '0';
    }
    if (this.maxIntegerDigits && intPart.length > this.maxIntegerDigits) {
      intPart = intPart.slice(0, this.maxIntegerDigits);
    }
    if (this.decimalPlaces !== null) {
      decPart = decPart.slice(0, this.decimalPlaces);
    }
    this.rawString = this.hasDecimal ? `${intPart}.${decPart}` : intPart;
    const formatted = this.formatFromString(intPart, decPart);
    input.value = formatted;
    if (isCursorAtEnd) {
      requestAnimationFrame(() => {
        const end = formatted.length;
        input.setSelectionRange(end, end);
      });

      const num = Number(this.rawString);
      this.onChange(isNaN(num) ? null : num);
      return;
    }
    let digitCount = 0;
    let newCursor = 0;
    let passedDot = false;
    for (let i = 0; i < formatted.length; i++) {
      const char = formatted[i];
      if (char === this.decimalSeparator) {
        passedDot = true;
        if (hasDotBeforeCursor && digitCount === digitCursor) {
          newCursor = i + 1;
          break;
        }
      }
      if (/\d/.test(char)) {
        digitCount++;
      }
      if (!hasDotBeforeCursor && digitCount >= digitCursor) {
        newCursor = i + 1;
        break;
      }
      if (hasDotBeforeCursor && passedDot && digitCount >= digitCursor) {
        newCursor = i + 1;
        break;
      }
    }
    if (digitCursor === 0) newCursor = 0;
    requestAnimationFrame(() => {
      input.setSelectionRange(newCursor, newCursor);
    });
    const num = Number(this.rawString);
    this.onChange(isNaN(num) ? null : num);
  }

  @HostListener('blur')
  onBlur(): void {
    if (!this.rawString) {
      this.onChange(null);
      this.el.nativeElement.value = '';
      this.onTouched();
      return;
    }
    let [intPart, decPart = ''] = this.rawString.split('.');
    if (this.hasDecimal) {
      decPart = decPart.padEnd(this.decimalPlaces!, '0');
    } else {
      decPart = '0'.repeat(this.decimalPlaces!);
    }
    this.hasDecimal = true;
    this.rawString = `${intPart}.${decPart}`;
    this.el.nativeElement.value = this.formatFromString(intPart, decPart);
    const num = Number(this.rawString);
    this.onChange(isNaN(num) ? null : num);
    this.onTouched();
  }

  private format(value: number | null): string {
    if (value === null || value === undefined) return '';
    if (typeof value !== 'number' || isNaN(value)) return '';
    if (!this.decimalPlaces) return '';
    const fixed = value.toFixed(this.decimalPlaces);
    const [intPart, decPart] = fixed.split('.');
    const grouped = this.groupInteger(intPart);
    return decPart ? grouped + this.decimalSeparator + decPart : grouped;
  }

  private parse(value: string): number | null {
    if (!value || !this.thousandsSeparator || !this.decimalSeparator || !this.groupPattern) return null;

    // remove all non-digits except decimal separator
    const regex = new RegExp(`[^0-9${this.escapeRegex(this.decimalSeparator)}]`, 'g');
    const cleaned = value.replace(regex, '');

    const normalized = value
      .split(this.thousandsSeparator).join('')
      .replace('.', this.decimalSeparator);

    const num = Number(normalized);

    return isNaN(num) ? null : num;
  }

  // Utility: escape regex special characters
  private escapeRegex(str: string): string {
    return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  }

  private groupInteger(intPart: string): string {
    const pattern = this.groupPattern; // e.g. [3,2]
    const separator = this.thousandsSeparator;

    if (!pattern) return intPart;

    let result = '';
    let index = intPart.length;
    let patternIndex = 0;

    while (index > 0) {
      const size =
        pattern[patternIndex] ??
        pattern[pattern.length - 1]; // repeat last size

      const start = Math.max(index - size, 0);
      const chunk = intPart.substring(start, index);

      result = result ? chunk + separator + result : chunk;

      index -= size;
      patternIndex++;
    }

    return result;
  }

  private formatFromString(intPart: string, decPart: string): string {
    const grouped = this.groupInteger(intPart);
    if (this.hasDecimal) {
      return grouped + this.decimalSeparator + decPart;
    }
    return grouped;
  }
}
